"use client";

import { useState, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { motion, AnimatePresence } from "motion/react";
import { ErrorBoundary } from "@/components/error-boundary";
import { AppShell } from "@/components/nav/app-shell";
import { LandingPage } from "@/components/onboarding";
import { ChatInterfaceWrapper } from "@/components/chat-interface-wrapper";

interface PageContentProps {
  currentChatId?: string;
  hasInitialMessages: boolean;
}

export function PageContent({ currentChatId, hasInitialMessages }: PageContentProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const forceLanding = searchParams.get("landing") === "1";
  const [showLanding, setShowLanding] = useState(true);
  const [hasInteracted, setHasInteracted] = useState(false);

  useEffect(() => {
    if (currentChatId || hasInitialMessages) {
      setShowLanding(false);
      setHasInteracted(true);
    }
  }, [currentChatId, hasInitialMessages]);

  useEffect(() => {
    if (currentChatId || hasInitialMessages) return;
    try {
      if (forceLanding) {
        setHasInteracted(false);
        setShowLanding(true);
        return;
      }
      const onboarded = localStorage.getItem("mindup-onboarded") === "true";
      if (onboarded) {
        setHasInteracted(true);
        setShowLanding(false);
      }
    } catch {
      // Ignore localStorage failures.
    }
  }, [currentChatId, hasInitialMessages, forceLanding]);

  const handleGetStarted = () => {
    setHasInteracted(true);
    localStorage.setItem("mindup-onboarded", "true");
    if (forceLanding) {
      router.replace("/");
    }
    setTimeout(() => {
      setShowLanding(false);
    }, 100);
  };

  return (
    <AnimatePresence mode="wait">
      {showLanding && !hasInteracted ? (
        <motion.div
          key="landing"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.5 }}
          className="min-h-screen"
        >
          <LandingPage onGetStarted={handleGetStarted} />
        </motion.div>
      ) : (
        <motion.div
          key="app"
          initial={{ opacity: 0, scale: 1.02 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="min-h-screen"
        >
          <ErrorBoundary fallbackTitle="Chat crashed">
            <AppShell>
              <ChatInterfaceWrapper />
            </AppShell>
          </ErrorBoundary>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

